import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal, For } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const CheckboxPage: Component = () => {
  const [frutas, setFrutas] = createSignal<string[]>(['banana'])
  const [termsAccepted, setTermsAccepted] = createSignal(false)

  const handleFrutasChange = (fruta: string) => {
    if (frutas().includes(fruta)) {
      setFrutas(frutas().filter(f => f !== fruta))
    } else {
      setFrutas([...frutas(), fruta])
    }
  }

  return (
    <>
      <Title>Checkbox Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Checkbox</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Componentes de selección binaria y múltiple. El checkbox permite a los usuarios elegir uno o más elementos de un conjunto de opciones, o alternar estados de confirmación.
          </p>
        </div>

        <div class="space-y-12">
          {/* Uso Básico y Estados */}
          <Card>
            <Card.Header
              title="Implementación y Estados"
              subtitle="Controla la selección, estados indeterminados y validaciones visuales."
              icon={<Icon icon="material-symbols-light:check-box-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-8 bg-surface-muted rounded-xl border border-border">
                <div class="space-y-6">
                  <h4 class="font-bold text-xs text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Estados de Interacción</h4>
                  <div class="space-y-4 px-3">
                    <Checkbox label="Acepto los términos y condiciones" checked={termsAccepted()} onChange={() => setTermsAccepted(!termsAccepted())} />
                    <Checkbox label="Opción pre-seleccionada" checked />
                    <Checkbox label="Estado indeterminado" indeterminate />
                    <Checkbox label="Componente desactivado" disabled />
                    <Checkbox label="Opción con error" error="Este campo es obligatorio" />
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="font-bold text-xs text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-secondary-500">Tooltip Integrado</h4>
                  <div class="space-y-6 px-3">
                    <Checkbox label="Información adicional" tooltip="El tooltip te ayuda a explicar opciones complejas" />
                    <Checkbox label="Tooltip inferior" tooltip="Ubicación personalizada del tooltip" tooltipPlacement="bottom" />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Variantes de Diseño */}
          <Card>
            <Card.Header
              title="Escalabilidad y Color"
              subtitle="Adapta el tamaño y el tono del checkbox para integrarlo en cualquier diseño."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10">
                <div class="space-y-6">
                  <h4 class="font-bold text-sm text-foreground-subtle">Dimensiones (Size)</h4>
                  <div class="flex flex-wrap items-center gap-10 p-8 bg-surface rounded-xl border border-border shadow-sm transition-all">
                    <div class="text-center space-y-2">
                      <Checkbox size="xs" checked />
                      <p class="text-[10px] font-mono font-bold text-foreground-subtle">xs</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Checkbox size="sm" checked />
                      <p class="text-[10px] font-mono font-bold text-foreground-subtle">sm</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Checkbox size="md" checked />
                      <p class="text-[10px] font-mono font-bold text-foreground-subtle">md</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Checkbox size="lg" checked />
                      <p class="text-[10px] font-mono font-bold text-foreground-subtle">lg</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Checkbox size="xl" checked />
                      <p class="text-[10px] font-mono font-bold text-foreground-subtle">xl</p>
                    </div>
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="font-bold text-sm text-foreground-subtle">Paleta Cromática (Color)</h4>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 bg-surface-muted rounded-xl border border-border shadow-inner">
                    <Checkbox label="Primary" color="primary" checked />
                    <Checkbox label="Secondary" color="secondary" checked />
                    <Checkbox label="Success" color="success" checked />
                    <Checkbox label="Danger" color="danger" checked />
                    <Checkbox label="Warning" color="warning" checked />
                    <Checkbox label="Info" color="info" checked />
                    <Checkbox label="Default" color="default" checked />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Ejemplo Real: Filtros */}
          <Card>
            <Card.Header
              title="Caso Práctico: Filtros de Sección"
              subtitle="Visualiza cómo agrupar opciones de selección múltiple con estados dinámicos."
              icon={<Icon icon="material-symbols-light:filter-list" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-2xl mx-auto p-10 bg-surface rounded-2xl border border-border shadow-xl overflow-hidden relative group">
                <div class="absolute top-0 right-0 p-6 opacity-5 pointer-events-none transition-opacity group-hover:opacity-10">
                  <Icon icon="material-symbols-light:menu" size={140} />
                </div>
                <div class="relative">
                  <h3 class="text-xl font-bold mb-6 flex items-center gap-2">
                    <Icon icon="material-symbols-light:eco-outline" class="text-emerald-500" /> Selección de Inventario
                  </h3>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <For each={['Manzana', 'Banana', 'Fresa', 'Uva']}>
                      {item => (
                        <div class="p-3 rounded-xl border border-border hover:bg-surface-hover transition-all cursor-pointer" onClick={() => handleFrutasChange(item.toLowerCase())}>
                          <Checkbox label={item} checked={frutas().includes(item.toLowerCase())} class="pointer-events-none" />
                        </div>
                      )}
                    </For>
                  </div>
                  <div class="mt-8 pt-6 border-t border-border flex flex-wrap gap-2">
                    <span class="text-xs text-foreground-subtle font-medium uppercase tracking-widest w-full mb-1">Seleccionados:</span>
                    <For each={frutas()}>
                      {f => (
                        <Badge color="primary" variant="soft" class="capitalize">
                          {f}
                        </Badge>
                      )}
                    </For>
                    {frutas().length === 0 && <p class="text-xs text-foreground-subtle italic">No hay selección activa.</p>}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Manual técnico de las propiedades disponibles para Checkbox."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">checked</td>
                      <td class="px-6 py-4">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Estado de selección del control.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">indeterminate</td>
                      <td class="px-6 py-4">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 text-balance italic">Representación de estados mixtos en grupos jerárquicos.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">label</td>
                      <td class="px-6 py-4">string | JSX.Element</td>
                      <td class="px-6 py-4">-</td>
                      <td class="px-6 py-4 font-bold italic">RECOMENDADO. Texto identificativo junto al control.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4">xs | sm | md | lg | xl</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Dimensión física del checkbox.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">color</td>
                      <td class="px-6 py-4 italic">MainColorsType</td>
                      <td class="px-6 py-4 font-mono">'primary'</td>
                      <td class="px-6 py-4">Color de relleno al estar activo.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">error</td>
                      <td class="px-6 py-4">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Activa estilos semánticos de error y validación.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default CheckboxPage
